// src/components/Credits.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}));

const BalanceCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  borderRadius: 24,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,183,27,0.15) 0%, transparent 70%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '10%',
    width: 250,
    height: 250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,183,27,0.08) 0%, transparent 70%)',
  },
}));

const PackageCard = styled(RoundedCard)(({ theme, $highlighted }) => ({
  background: $highlighted
    ? 'linear-gradient(135deg, #fffdf5 0%, #fff8e7 100%)'
    : '#ffffff',
  border: $highlighted ? '2px solid #ffb71b' : '1px solid #e8e8e8',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeIn} 0.4s ease-out`,
  padding: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-4px)',
    borderColor: '#ffb71b',
  },
}));

const BuyButton = styled(Button)(({ $highlighted }) => ({
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  padding: '12px 32px',
  width: '100%',
  fontSize: '0.95rem',
  background: $highlighted
    ? 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)'
    : '#f5f5f5',
  color: $highlighted ? '#000' : '#333',
  boxShadow: $highlighted ? '0 4px 16px rgba(255, 183, 27, 0.3)' : 'none',
  '&:hover': {
    background: $highlighted
      ? 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)'
      : '#e8e8e8',
    boxShadow: $highlighted ? '0 6px 24px rgba(255, 183, 27, 0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
}));

const IconCircle = styled(Box)(({ $bgColor = '#fff8e7', $size = 64 }) => ({
  width: $size,
  height: $size,
  borderRadius: 20,
  backgroundColor: $bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
}));

const DEFAULT_PACKAGES = [
  { credits: 100, price: 25, label: 'Starter', highlighted: false, badge: '', features: ['100 notification credits', 'Standard support'] },
  { credits: 220, price: 50, label: 'Basic', highlighted: false, badge: '', features: ['220 notification credits', 'Email support'] },
  { credits: 480, price: 100, label: 'Growth', highlighted: true, badge: 'Most Popular', features: ['480 notification credits', 'Priority delivery', 'Priority support'] },
  { credits: 2500, price: 400, label: 'Scale', highlighted: false, badge: 'Best Value', features: ['2,500 notification credits', 'Best price per credit', 'Dedicated support'] },
];

const PACKAGE_ICONS = [
  { icon: <BoltIcon sx={{ color: '#1976d2', fontSize: 30 }} />, iconBg: '#e3f2fd' },
  { icon: <LocalFireDepartmentIcon sx={{ color: '#ff9800', fontSize: 30 }} />, iconBg: '#fff3e0' },
  { icon: <StarIcon sx={{ color: '#ffb71b', fontSize: 30 }} />, iconBg: '#fff8e7' },
  { icon: <DiamondIcon sx={{ color: '#9c27b0', fontSize: 30 }} />, iconBg: '#f3e5f5' },
  { icon: <MonetizationOnIcon sx={{ color: '#4caf50', fontSize: 30 }} />, iconBg: '#e8f5e9' },
];

const DEFAULT_CONTENT = {
  sectionTitle: 'Buy Credits',
  sectionSubtitle: 'Choose a package that fits your needs',
  balanceLabel: 'Your Credit Balance',
  balanceChip: 'Credits are used for sending notifications and promotions',
};

export default function Credits() {
  const [credits, setCredits] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);
  const [pageContent, setPageContent] = useState(DEFAULT_CONTENT);

  // Get userId from URL
  const pathParts = window.location.pathname.split('/');
  const userId = pathParts[pathParts.length - 1];

  // Load credit balance and CMS content
  useEffect(() => {
    if (userId) {
      const token = localStorage.getItem('token');
      fetch(`/api/vendor/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.vendor?.credits !== undefined) setCredits(data.vendor.credits);
        })
        .catch(() => {});
    }

    fetch('/api/credits-content')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Object.keys(data).length > 1) {
          if (data.packages && data.packages.length > 0) setPackages(data.packages);
          setPageContent({
            sectionTitle: data.sectionTitle || DEFAULT_CONTENT.sectionTitle,
            sectionSubtitle: data.sectionSubtitle || DEFAULT_CONTENT.sectionSubtitle,
            balanceLabel: data.balanceLabel || DEFAULT_CONTENT.balanceLabel,
            balanceChip: data.balanceChip || DEFAULT_CONTENT.balanceChip,
          });
        }
      })
      .catch(() => {});
  }, [userId]);

  // Handle the redirect back from Stripe Checkout. On success we verify the
  // session server-side (which idempotently grants the credits) and refresh the
  // balance, then strip the query params from the URL.
  useEffect(() => {
    if (!userId) return;
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkout === 'success' && sessionId) {
      const token = localStorage.getItem('token');
      fetch(`/api/vendor/${userId}/credits/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.ok) {
            setCredits(data.credits);
            setPurchaseSuccess(true);
            setTimeout(() => setPurchaseSuccess(false), 5000);
          }
        })
        .catch(() => {})
        .finally(() => window.history.replaceState({}, '', `/credits/${userId}`));
    } else if (checkout === 'cancel') {
      setCheckoutError('Checkout cancelled — you have not been charged.');
      window.history.replaceState({}, '', `/credits/${userId}`);
    }
  }, [userId]);

  const handleBuyClick = (pkg, index) => {
    setSelectedPackage({ ...pkg, index });
    setConfirmOpen(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedPackage) return;
    setPurchasing(true);
    setCheckoutError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/vendor/${userId}/credits/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ packageIndex: selectedPackage.index }),
      });

      const data = await res.json();
      if (res.ok && data.url) {
        // Hand off to Stripe's hosted Checkout page.
        window.location.href = data.url;
        return;
      }
      setCheckoutError(data.message || 'Could not start checkout. Please try again.');
    } catch (err) {
      console.error(err);
      setCheckoutError('Could not start checkout. Please try again.');
    } finally {
      setPurchasing(false);
      setConfirmOpen(false);
      setSelectedPackage(null);
    }
  };

  const handleCancelPurchase = () => {
    setConfirmOpen(false);
    setSelectedPackage(null);
  };

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        {/* Balance Card */}
        <BalanceCard>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <AccountBalanceWalletIcon sx={{ color: '#ffb71b', fontSize: 28 }} />
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  {pageContent.balanceLabel}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff' }}>
                  {credits}
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                  credits
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={<MonetizationOnIcon sx={{ color: '#ffb71b !important' }} />}
              label={pageContent.balanceChip}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                fontSize: '0.85rem',
                py: 2.5,
                px: 1,
              }}
            />
          </Box>
        </BalanceCard>

        {purchaseSuccess && (
          <Alert
            icon={<CheckCircleIcon sx={{ fontSize: 24 }} />}
            severity="success"
            sx={{ mb: 3, borderRadius: 3, fontSize: '0.95rem' }}
            onClose={() => setPurchaseSuccess(false)}
          >
            Credits purchased successfully! Your new balance is {credits} credits.
          </Alert>
        )}

        {checkoutError && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 3, fontSize: '0.95rem' }}
            onClose={() => setCheckoutError('')}
          >
            {checkoutError}
          </Alert>
        )}

        {/* Section Title */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 0.5 }}>
            {pageContent.sectionTitle}
          </Typography>
          <Typography variant="body1" sx={{ color: '#888' }}>
            {pageContent.sectionSubtitle}
          </Typography>
        </Box>

        {/* Packages Grid — columns follow the number of packages so they fill
            the row evenly (no empty trailing slot when there are fewer than 5). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: `repeat(${Math.min(packages.length, 5)}, 1fr)`,
            },
            gap: 3,
          }}
        >
          {packages.map((pkg, index) => {
            const iconSet = PACKAGE_ICONS[index % PACKAGE_ICONS.length];
            const perCredit = pkg.credits > 0 ? (pkg.price / pkg.credits).toFixed(2) : '0.00';
            return (
              <PackageCard key={index} $highlighted={pkg.highlighted}>
                {pkg.badge && (
                  <Chip
                    label={pkg.badge}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: '#ffb71b',
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
                <IconCircle $bgColor={iconSet.iconBg}>
                  {iconSet.icon}
                </IconCircle>
                <Typography variant="subtitle2" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}>
                  {pkg.label}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#111', mb: 0.5 }}>
                  {pkg.credits}
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                  credits
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 0.5 }}>
                  ${pkg.price.toFixed(2)}{' '}
                  <Typography component="span" variant="caption" sx={{ color: '#999', fontWeight: 600 }}>AUD</Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa', mb: 2, display: 'block' }}>
                  ${perCredit} per credit
                </Typography>
                <Box sx={{ mb: 3, width: '100%' }}>
                  {pkg.features.map((feature, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, justifyContent: 'center' }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: pkg.highlighted ? '#ffb71b' : '#ccc' }} />
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <BuyButton
                  $highlighted={pkg.highlighted}
                  onClick={() => handleBuyClick(pkg, index)}
                >
                  Buy {pkg.credits} Credits
                </BuyButton>
              </PackageCard>
            );
          })}
        </Box>

        {/* Confirm Purchase Dialog */}
        <Dialog
          open={confirmOpen}
          onClose={handleCancelPurchase}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)', p: 3 }}>
            <DialogTitle sx={{ p: 0, color: '#000', fontWeight: 700 }}>
              Confirm Purchase
            </DialogTitle>
          </Box>
          <DialogContent sx={{ mt: 3, textAlign: 'center' }}>
            {selectedPackage && (
              <>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#111', mb: 1 }}>
                  {selectedPackage.credits} Credits
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffb71b', mb: 2 }}>
                  ${selectedPackage.price.toFixed(2)} AUD
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Your new balance will be {credits + selectedPackage.credits} credits.
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1.5 }}>
                  You'll be redirected to our secure Stripe checkout to complete payment.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCancelPurchase} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmPurchase}
              disabled={purchasing}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)',
                color: '#000',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)',
                },
              }}
            >
              {purchasing ? 'Redirecting…' : 'Continue to Payment'}
            </Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </PageContainer>
  );
}
